import { z } from "zod";

// ── Profile ───────────────────────────────────────────────────────────────────
//
// One profile per Firebase user. The profile `id` is the Firebase uid.
// Profiles are tenant-scoped — they live in the user's primary team tenant DB.
// They are NOT stored in Firestore — all profile data lives in MongoDB.

export const Profile = z.object({
  id: z.string().describe("Unique resource identifier"),
  user_id: z.string().describe("Unique user identifier"),
  email: z.string().email().describe("User's email address"),
  full_name: z.string().describe("User's full display name"),
  avatar_id: z.string().optional().describe("GridFS file ID for the user's avatar image"),
  avatar_url: z.string().optional().describe("Signed avatar download URL — computed, never stored"),
  last_selected_team_id: z
    .string()
    .nullable()
    .optional()
    .describe("Last team the user had selected in the app"),
  requires_password_change: z
    .boolean()
    .optional()
    .describe("When true the user must change their password on next login"),
  mfa_enrolled: z
    .boolean()
    .optional()
    .describe("Whether the user has enrolled in multi-factor authentication"),
  mfa_enrolled_at: z.coerce
    .date()
    .nullable()
    .optional()
    .describe("Timestamp when the user completed MFA enrollment"),
  created_at: z.date().describe("When the profile was created"),
  updated_at: z.date().describe("When the profile was last updated"),
});

export type Profile = z.infer<typeof Profile>;

export const ProfileCreate = Profile.omit({
  id: true, // auto-generated ObjectId
  created_at: true,
  updated_at: true,
}).partial({
  last_selected_team_id: true,
});

export type ProfileCreate = z.infer<typeof ProfileCreate>;

// ── SignupBody ────────────────────────────────────────────────────────────────
//
// Request body for POST /auth/signup.

export const SignupCreate = z.object({
  email: z.string().email().describe("User's email address"),
  password: z.string().min(6).describe("Password — minimum 6 characters"),
  full_name: z.string().min(1).describe("User's full display name"),
});

export type SignupCreate = z.infer<typeof SignupCreate>;

// ── SignupResult ──────────────────────────────────────────────────────────────
//
// Response body for POST /auth/signup.

export const SignupResult = z.object({
  uid: z.string().describe("Newly created user ID"),
  email: z.string().email().describe("User's email address"),
  team_id: z.string().describe("ID of the default team created for this user"),
});

export type SignupResult = z.infer<typeof SignupResult>;

// ── ProfileUpdate ─────────────────────────────────────────────────────────────

// ── ProfilePasswordReset ──────────────────────────────────────────────────────
//
// Response body for POST /profiles/:uid/reset-password.
// Returns the generated temp code to the caller so it can be delivered
// out-of-band to the user (email, SMS, etc.).

export const ProfilePasswordReset = z.object({
  temp_code: z
    .string()
    .describe(
      "Temporary one-time password — deliver to the user to allow login and password change",
    ),
});

export type ProfilePasswordReset = z.infer<typeof ProfilePasswordReset>;

// ── ProfileUpdate ─────────────────────────────────────────────────────────────

export const ProfileUpdate = z.object({
  full_name: z.string().optional().describe("User's full display name"),
  last_selected_team_id: z
    .string()
    .nullable()
    .optional()
    .describe("Last team the user had selected in the app"),
  requires_password_change: z
    .boolean()
    .optional()
    .describe("When true the user must change their password on next login"),
  mfa_enrolled: z
    .boolean()
    .optional()
    .describe("Whether the user has enrolled in multi-factor authentication"),
  mfa_enrolled_at: z.coerce
    .date()
    .nullable()
    .optional()
    .describe("Timestamp when the user completed MFA enrollment"),
});

export type ProfileUpdate = z.infer<typeof ProfileUpdate>;
